import { UseQueryOptions } from '@tanstack/react-query';
import { NavigationRoutes } from 'common/constants';
import { ApiError } from 'common/repositories/common.model';
import { useRouter } from 'next/router';
import qs from 'qs';
import React from 'react';
import merge from 'ts-deepmerge';

const isSalesList = [
  NavigationRoutes.TourSalesList,
  NavigationRoutes.TicketSalesList,
  NavigationRoutes.ActivitySalesList,
  NavigationRoutes.CruiseSalesList,
  NavigationRoutes.HotelSalesList,
  NavigationRoutes.ProductSalesList,
  NavigationRoutes.TravelDocumentSalesList,
];

const isIgnoreUrlParam = [
  NavigationRoutes.Cro,
  NavigationRoutes.ProductsView,
  NavigationRoutes.TourView,
  NavigationRoutes.TourDetailReport,
];

type ComposedVariable<QueryData, QueryInput, Extra> = Pick<
  UseQueryOptions<QueryData, ApiError>,
  'onSuccess' | 'onError'
> & { input: QueryInput } & { extra: Extra };

export default function useComposedQuery<
  QueryData,
  QueryInput extends AppliedQueryInput,
  QueryResult,
  AppliedQueryInput,
  Extra,
  ComposedQueryVariables extends ComposedVariable<
    QueryData,
    AppliedQueryInput,
    Extra
  >[],
>(
  useQuery: (
    input?: QueryInput,
    baseOptions?: UseQueryOptions<QueryData, ApiError>,
  ) => QueryResult,
  input?: QueryInput,
  ...composedVariables: ComposedQueryVariables
) {
  const { pathname, isReady } = useRouter();

  const params = React.useMemo(() => {
    const mergedParams = merge(
      ...composedVariables.map((v) => v.input || {}),
      input || {},
    ) as {
      params: QueryInput;
    };

    // sales list
    if (isSalesList.includes(pathname as NavigationRoutes)) {
      const _input = (input as any)?.params;
      const _inputFilter = _input?.filter;
      return {
        params: {
          ..._input,
          ...(composedVariables[1].input as any).params, // sort
          filter: {
            ...(composedVariables[0].input as any).params.filter,
            ..._inputFilter,
            payment_status: _inputFilter.payment_status, // sales paid status
            status: _inputFilter.status, // invoice status
          },
          status: _input.status, // document sales
        },
      };
    }

    return mergedParams;
  }, [composedVariables, input, pathname]);

  React.useEffect(() => {
    if (isIgnoreUrlParam.includes(pathname as NavigationRoutes)) return;
    if (!isReady) return;

    const newQuery = qs.stringify(params.params);
    const newUrl = `${pathname}?${newQuery}`;
    const newHistoryState = {
      ...window.history.state,
      url: newUrl,
      as: newUrl,
    };

    window.history.replaceState(newHistoryState, '', newUrl);
  }, [pathname, isReady, params]);

  const result = useQuery(
    {
      ...params,
    } as QueryInput,
    {
      onSuccess(...args) {
        composedVariables.map(({ onSuccess }) => {
          onSuccess && onSuccess(...args);
        });
      },
      onError(...args) {
        composedVariables.map(({ onError }) => {
          onError && onError(...args);
        });
      },
    },
  );

  const extras = React.useMemo(() => {
    return composedVariables.map((v) => v.extra) as {
      [Key in keyof ComposedQueryVariables]: ComposedQueryVariables[Key] extends ComposedVariable<
        QueryData,
        QueryInput,
        infer U
      >
        ? U
        : any;
    };
  }, [composedVariables]);

  const values = React.useMemo(() => {
    return {
      ...result,
      query: result,
      extras,
    };
  }, [extras, result]);

  return values;
}
